import { useState } from 'react'
import { format } from 'date-fns'
import { UtensilsCrossed } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/utils/date'
import { PageHeader } from '@/components/layout/PageHeader'
import { BookingTimeline } from '@/components/bookings/BookingTimeline'
import { BookingDetailPanel } from '@/components/bookings/BookingDetailPanel'
import { BookingForm } from '@/components/bookings/BookingForm'
import { WalkInModal } from '@/components/bookings/WalkInModal'
import { ArrivalsWidget } from '@/components/dashboard/ArrivalsWidget'
import { RequestsWidget } from '@/components/dashboard/RequestsWidget'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { useTodayBookings, useBookings } from '@/lib/queries/useBookings'
import { useAreas } from '@/lib/booking/tableAdapter'
import { useBookingOpeningHours } from '@/lib/booking/scheduleAdapter'
import { useDashboardTour } from '@/hooks/useDashboardTour'

const EMPTY: never[] = []

export default function DashboardPage() {
  const { t } = useTranslation()
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  const [bookingFormOpen, setBookingFormOpen] = useState(false)
  const [showWalkIn, setShowWalkIn] = useState(false)
  const [walkInDefaultTableIds, setWalkInDefaultTableIds] = useState<string[]>([])
  const [clearPreviewSignal, setClearPreviewSignal] = useState(0)
  const bumpClearPreview = () => setClearPreviewSignal((s) => s + 1)
  const [newBookingDefaults, setNewBookingDefaults] = useState<{ date?: string; time?: string; duration?: number; tableIds?: string[] } | undefined>()
  const today = format(new Date(), 'yyyy-MM-dd')

  const handleCreateBooking = (tableDocumentId: string, time: string, duration: number) => {
    const [h, m] = time.split(':').map(Number)
    const slotMinutes = h * 60 + m
    const now = new Date()
    const nowMinutes = now.getHours() * 60 + now.getMinutes()
    const isNearNow = Math.abs(slotMinutes - nowMinutes) <= 60
    if (isNearNow) {
      setWalkInDefaultTableIds([tableDocumentId])
      setShowWalkIn(true)
    } else {
      setNewBookingDefaults({ date: today, time, duration, tableIds: [tableDocumentId] })
      setBookingFormOpen(true)
    }
  }

  const { data: bookingsResp, isLoading } = useTodayBookings()
  const { data: requestsResp } = useBookings({ status: ['request'], bookingKind: 'reservation', pageSize: 100 })
  const { data: areasResp } = useAreas()
  const { data: ohResp } = useBookingOpeningHours()

  const bookings     = bookingsResp?.data ?? EMPTY
  const requests     = requestsResp?.data ?? EMPTY
  const areas        = areasResp?.data    ?? EMPTY
  const openingHours = ohResp?.data       ?? EMPTY

  // useDashboardTour(!isLoading)

  return (
    <div className="relative">
      <PageHeader
        title={t('dashboard.title')}
        description={`${format(new Date(), 'EEEE, MMMM d', { locale: getDateLocale() })} — ${t('dashboard.bookings', { count: bookings.length })}`}
      />

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Timeline — 8/10 */}
        <div id="timeline-container" className="w-full min-w-0 overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] lg:flex-[8]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border-subtle)]">
            <div>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">{t('dashboard.todaysTimeline')}</p>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{format(new Date(), 'EEEE, MMMM d', { locale: getDateLocale() })}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowWalkIn(true)}>
                <UtensilsCrossed className="h-3.5 w-3.5" />
                Walk-in
              </Button>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]">
                {t('dashboard.bookings', { count: bookings.length })}
              </span>
            </div>
          </div>
          <div className="p-4">
            {isLoading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <BookingTimeline
                bookings={bookings}
                areas={areas}
                openingHours={openingHours}
                date={today}
                onBookingClick={setSelectedDocumentId}
                onCreateBooking={handleCreateBooking}
                clearPreviewSignal={clearPreviewSignal}
              />
            )}
          </div>
        </div>

        {/* Widgets — 2/10 */}
        <div className="grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:w-auto lg:flex-[2] lg:flex-col lg:min-w-[180px]">
          <ArrivalsWidget todayBookings={bookings} />
          <RequestsWidget requests={requests} />
        </div>
      </div>

      <BookingDetailPanel
        documentId={selectedDocumentId}
        onClose={() => setSelectedDocumentId(null)}
      />

      <BookingForm
        open={bookingFormOpen}
        onClose={() => { setBookingFormOpen(false); setNewBookingDefaults(undefined); bumpClearPreview() }}
        onSwitchToWalkIn={() => {
          setWalkInDefaultTableIds(newBookingDefaults?.tableIds ?? [])
          setBookingFormOpen(false)
          setNewBookingDefaults(undefined)
          setShowWalkIn(true)
        }}
        defaults={newBookingDefaults}
      />

      <WalkInModal
        open={showWalkIn}
        onClose={() => { setShowWalkIn(false); setWalkInDefaultTableIds([]); bumpClearPreview() }}
        defaultTableIds={walkInDefaultTableIds}
      />
    </div>
  )
}
